// contexts/AdContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { FetchAddService } from '../utils/services/chat.service';
import { useToken } from '../providers/TokenProvider';

interface AdImage {
    url: string;
    placement: string;
}

interface AdContextType {
    ads: AdImage[];
    loading: boolean;
    error: boolean;
}

const AdContext = createContext<AdContextType>({ ads: [], loading: true, error: false });

export const AdProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [ads, setAds] = useState<AdImage[]>([]);
    const [loading, setLoading] = useState(true);
    const { token, tokenExpiry } = useToken();

    useEffect(() => {
        if (!token) return;

        const hasFetchedAds = sessionStorage.getItem('ads_fetched');
        const savedAds = sessionStorage.getItem('cached_ads');

        // Only fetch once per session (or if token changed)
        if (hasFetchedAds && savedAds) {
            setAds(JSON.parse(savedAds));
            setLoading(false);
            return;
        }

        const fetchAds = async () => {
            try {
                const { code, data } = await FetchAddService({});
                if (code === 200 && data?.images) {
                    const images = data.images as AdImage[];
                    setAds(images);
                    sessionStorage.setItem('cached_ads', JSON.stringify(images));
                    sessionStorage.setItem('ads_fetched', 'true');
                }
            } catch (err) {
                console.error('Failed to fetch ads', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAds();
    }, [token]);

    // Clear on token expiry
    useEffect(() => {
        if (!tokenExpiry) {
            sessionStorage.removeItem('cached_ads');
            sessionStorage.removeItem('ads_fetched');
        }
    }, [tokenExpiry]);

    return (
        <AdContext.Provider value={{ ads, loading, error: false }}>
            {children}
        </AdContext.Provider>
    );
};

export const useAds = () => useContext(AdContext);